import { FormSection, SpecialFormField } from "../form/types";

import { underdotsCommonSchema, underdotsPublicSchema } from "./types/schema";

const gprCollectionDetails: FormSection<
  | "collectionDate"
  | "cors"
  | "collectionParameters"
  | "surfaceWet"
  | "radarMake"
> = {
  title: "GPR collection details",
  description:
    "Please provide details on how the data was collected by the operator.",
  required: false,
  displayColumns: 2,
  formFields: [
    {
      name: "radarMake",
      label: "Radar make",
      description: "The make of the  of the GPR array used to capture the data",
      required: false,
      formFieldType: "select",
      selectOptions: [
        { label: "Kontur", value: "Kontur" },
        { label: "IDS", value: "IDS" },
        { label: "Impulse", value: "Impulse" }
      ]
    },
    {
      name: "cors",
      label: "Coordinate Reference System (CRS)",
      required: false,
      formFieldType: "text",
      description:
        "E.g., EPSG:28356, EPSG: 2193, or other. If a specific value is not supplied and we are unable to determine CRS used based on your shared files, we will look for the most appropriate local CRS for your area."
    },
    {
      name: "collectionDate",
      label: "Collection date",
      required: false,
      formFieldType: "date",
      description: "Date when you started your GPR scanning work."
    },

    {
      name: "collectionParameters",
      label: "Collection parameters",
      required: false,
      formFieldType: "text",
      description:
        "These may include antenna frequency, time window, sample interval, samples per trace, step size, stacking, and transect spacing and orientation. This information helps us evaluate your GPR files and determine whether a meaningful point-cloud visualisation can be produced."
    },
    {
      name: "surfaceWet",
      label: "Was the surface you surveyed wet?",
      required: false,
      formFieldType: "select",
      description:
        "This information helps us process your GPR files into a more meaningful point-cloud visualisation.",
      selectOptions: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
        { label: "I don't know", value: "I don't know" }
      ]
    }
  ]
};

const postProcessing: FormSection<
  | "preProcessed"
  | "radarPositioningMethod"
  | "postProcessed"
  | "softwareUsed"
  | "radarSampleDepth"
  | "elevationModel"
> = {
  title: "Post processing",
  description:
    "Please provide any details on how the GPR data has been processed prior to Underdots processing.",
  required: false,
  displayColumns: 2,

  formFields: [
    {
      name: "preProcessed",
      label: "Has the data been pre-processed before upload?",
      required: false,
      formFieldType: "select",
      description:
        "Data in SEG-Y format is preferred - if you are providing raw data, we may contact you to confirm some processing settings.",
      selectOptions: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
        { label: "I don't know", value: "I don't know" }
      ]
    },
    {
      name: "radarPositioningMethod",
      label: "Radar positioning method",
      required: false,
      formFieldType: "text",
      description:
        "E.g., GPS, Total Station, IMU, Combined. Please be sure to include relevant files within your folder, especially if supplying data that has not been processed."
    },
    {
      name: "postProcessed",
      label: "Has the positioning data been post-processed?",
      required: false,
      formFieldType: "select",
      selectOptions: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
        { label: "I don't know", value: "I don't know" }
      ]
    },
    {
      name: "softwareUsed",
      label: "Processing software used",
      required: false,
      formFieldType: "text",
      description:
        "E.g., Examiner, IQ Maps, other information is helpful when we need to troubleshoot data issues and artifacts."
    },
    {
      name: "radarSampleDepth",
      label: "Radar sample depth (m)",
      required: false,
      formFieldType: "number"
    },
    {
      name: "elevationModel",
      label: "Are you providing an elevation model?",
      required: false,
      formFieldType: "select",
      description:
        "If you have a Digital Elevation Model (DEM) you would like us to use, please include it with your shared files. If specific DEM is not supplied, we will use the latest version of ASTER GDEM from Earthdata.nasa.gov",
      selectOptions: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" }
      ]
    }
  ]
};

const extraInfo: FormSection<"notes"> = {
  title: "Extra info",
  description:
    "Please provide any other relevant info we might need to process your data",
  required: false,
  formFields: [
    {
      name: "notes",
      label: "Notes",
      required: false,
      formFieldType: "textarea"
    }
  ]
};

const projectContact: FormSection<"fullName" | "email"> = {
  title: "Project contact",
  required: true,
  formFields: [
    {
      name: "fullName",
      label: "Your name",
      required: true,
      formFieldType: "text"
    },
    {
      name: "email",
      label: "Email address",
      required: true,
      formFieldType: "text"
    }
  ]
};

const files: FormSection<"dataLink"> = {
  title: "Files",
  description:
    "Your GPR files may be in one of the following formats: .3DR, SEG-Y, GeoTIFF.",
  required: true,
  formFields: [
    {
      name: "dataLink",
      label: "Link to scan files",
      required: true,
      formFieldType: "text",
      description:
        "Please be sure to include associated GPS records, if they aren't already embedded. Please check that only the files relevant to this request are included. We will review all data and get back to you if we have any questions.",
      helperText: "Include the leading https://, E.g., https://www.example.com."
    }
  ]
};

export const UNDERDOTS_PORTAL_FORM: (
  | FormSection<keyof typeof underdotsCommonSchema>
  | SpecialFormField
)[] = [
  "name",
  "orgId",
  "scope",
  gprCollectionDetails,
  postProcessing,
  extraInfo
];

export const UNDERDOTS_FORM: FormSection<keyof typeof underdotsPublicSchema>[] =
  [projectContact, gprCollectionDetails, files, postProcessing, extraInfo];
